import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Clock, ArrowRight } from 'lucide-react';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import WaitlistForm from '@/components/WaitlistForm';
import HealthAlertBanner from '@/components/HealthAlertBanner';

/**
 * HealthLoop Landing Page — short, calm, NHS-safe.
 * Single clear CTA, three bullet benefits, link to How It Works.
 */
const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50" data-testid="landing-page">
      <div className="max-w-3xl mx-auto px-6 py-12 sm:py-20">

        {/* Brand */}
        <div className="flex items-center justify-between mb-16">
          <HealthLoopLogo size="md" showText={true} />
          <button
            onClick={() => navigate('/login')}
            className="text-sm font-medium text-gray-700 hover:text-emerald-700 transition-colors"
            data-testid="landing-signin-btn"
          >
            Sign in
          </button>
        </div>

        {/* Active global health alert (WHO / NHS) — dismissible per device */}
        <HealthAlertBanner />

        {/* Hero — leads with the brand positioning to differentiate
            HealthLoop from unrelated products that share the name
            (hospital patient-monitoring, post-procedure recovery, etc.).
            See /app/memory/PRD.md "Brand Differentiation" entry. */}
        <div className="space-y-5 mb-10" data-testid="landing-hero">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">
            HealthLoop
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1]">
            A Digital Preventive Health Education Platform
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl">
            Empowering healthier lives through preventive health education,
            wellness engagement, and informed decision-making.
          </p>
          <ul
            className="flex flex-wrap gap-2 pt-2"
            aria-label="Platform pillars"
            data-testid="landing-pillars"
          >
            {['Preventive Health', 'Education', 'Engagement', 'Wellness'].map((pillar) => (
              <li
                key={pillar}
                className="inline-flex items-center text-xs sm:text-sm font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1"
              >
                {pillar}
              </li>
            ))}
          </ul>
          <p className="text-base text-gray-600 leading-relaxed max-w-2xl pt-2 italic">
            Small steps today. Better health tomorrow. Access trusted
            preventive health education, wellness insights, and practical
            guidance that empower healthier decisions every day.
          </p>
        </div>

        {/* Primary CTA */}
        <div className="mb-12">
          <Button
            onClick={() => navigate('/login')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-7 h-14 text-base font-semibold rounded-xl shadow-lg"
            data-testid="landing-cta-btn"
          >
            Start your wellness journey
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="mt-3 text-sm text-gray-600 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Free preventive health education · Made for everyday wellbeing
          </p>
        </div>

        {/* Differentiator strip — explicit positioning vs unrelated
            "HealthLoop" products (hospital monitoring, clinical care
            coordination). Placed directly after the CTA so it's the very
            first scroll surface a search-engine-arriving visitor sees. */}
        <section
          className="mb-12 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-white p-6 sm:p-8"
          data-testid="landing-differentiator"
        >
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">
            Prevention, not treatment
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
            Focused on prevention, not treatment.
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-3">
            HealthLoop empowers individuals through preventive health
            education, wellness engagement, health literacy, and informed
            decision-making — <em>before</em> health challenges arise.
          </p>
          <p className="text-base text-gray-700 leading-relaxed">
            Unlike hospital monitoring systems or clinical care-coordination
            platforms, HealthLoop focuses on helping people build healthier
            habits, improve health awareness, and make informed choices for
            long-term wellbeing.
          </p>
        </section>

        {/* Our Mission — explicit public-health framing for SEO + visitors */}
        <section className="mb-12" data-testid="landing-mission">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Our mission
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-5 max-w-3xl">
            To make preventive health education accessible, engaging, and
            actionable for individuals, families, and communities worldwide.
          </p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Focus areas
          </p>
          <ul
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5"
            aria-label="Focus areas"
            data-testid="landing-mission-focus-areas"
          >
            {[
              'Preventive Health',
              'Health Education',
              'Wellness Engagement',
              'Maternal Health',
              'Child Health',
              'Health Literacy',
              'Community Wellbeing',
            ].map((area) => (
              <li
                key={area}
                className="inline-flex items-center gap-2 text-sm text-gray-700"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                {area}
              </li>
            ))}
          </ul>
        </section>

        {/* Three core value props — public-health framing. Replaces the
            earlier habit-tracker language ("one minute a day", "gentle
            nudge", "reflect don't judge") to align with HealthLoop's
            Digital Preventive Health Education Platform positioning. */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          <div className="border-l-2 border-emerald-200 pl-4" data-testid="benefit-1">
            <p className="text-sm font-semibold text-gray-900 mb-1">Learn every day</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Access practical preventive health education, wellness insights,
              and evidence-informed guidance designed to support healthier
              choices and long-term wellbeing.
            </p>
          </div>
          <div className="border-l-2 border-emerald-200 pl-4" data-testid="benefit-2">
            <p className="text-sm font-semibold text-gray-900 mb-1">Stay informed</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Receive timely health education prompts, wellness reminders,
              and awareness tips that encourage proactive health decisions.
            </p>
          </div>
          <div className="border-l-2 border-emerald-200 pl-4" data-testid="benefit-3">
            <p className="text-sm font-semibold text-gray-900 mb-1">Build health awareness</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Track your learning journey, strengthen your health literacy,
              and develop habits that support lifelong wellbeing.
            </p>
          </div>
        </div>

        {/* Why HealthLoop Exists — mission-grounded public-health context.
            Sits between the three value props and the waitlist so visitors
            (and Google's text scanner) read the *why* before the *what*. */}
        <section className="mb-12" data-testid="landing-why-exists">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
            Why HealthLoop exists
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-3 max-w-3xl">
            Millions of preventable health conditions can be reduced through
            better health awareness, early action, and informed
            decision-making.
          </p>
          <p className="text-base text-gray-700 leading-relaxed mb-3 max-w-3xl">
            HealthLoop was created to make preventive health education
            accessible, engaging, and actionable for individuals, families,
            and communities worldwide.
          </p>
          <p className="text-base text-gray-700 leading-relaxed max-w-3xl">
            Our goal is not to diagnose illness, monitor patients, or
            replace healthcare professionals. Our goal is to empower people
            with trusted health knowledge <em>before</em> health challenges
            arise.
          </p>
        </section>

        {/* Founder Story — anchors the brand in a real public-health
            professional, which Google's Organization schema cross-references
            via the `founder` field in /public/index.html. */}
        <section
          className="mb-12 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-6 sm:p-7"
          data-testid="landing-founder"
        >
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">
            Founder
          </p>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
            Founded by a public health professional
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-3 max-w-3xl">
            HealthLoop was founded by{' '}
            <strong className="text-gray-900">Esther Olamide Amorighoye</strong>,
            a Public Health professional committed to improving health
            awareness, preventive health education, and community wellbeing
            through accessible digital solutions.
          </p>
          <p className="text-base text-gray-700 leading-relaxed max-w-3xl">
            This platform was created to help individuals and families make
            informed health decisions through education, engagement, and
            empowerment.
          </p>
        </section>

        {/* Waitlist form */}
        <div className="mb-12 bg-white border border-gray-100 rounded-2xl p-6 sm:p-7 shadow-sm">
          <WaitlistForm source="landing_page" />
        </div>

        {/* Secondary links */}
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <button
            onClick={() => navigate('/how-it-works')}
            className="text-emerald-700 font-semibold hover:underline inline-flex items-center gap-1"
            data-testid="landing-how-it-works-link"
          >
            How HealthLoop works
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Medical disclaimer — explicit on the public landing so first-time
            visitors clearly see the platform's educational scope and where
            to escalate when urgent. Covers all four required points:
            preventive education only, not a diagnosis tool, not an
            emergency service, and the emergency numbers to call. */}
        <p
          className="text-xs text-gray-500 leading-relaxed mt-6 max-w-2xl"
          data-testid="landing-medical-disclaimer"
        >
          <strong className="text-gray-700">A note on scope:</strong>{' '}
          HealthLoop is a <strong className="font-semibold">preventive health education platform</strong>.
          It is <strong className="font-semibold">not a diagnostic tool</strong> and is{' '}
          <strong className="font-semibold">not an emergency medical service</strong>.
          For diagnosis, treatment or anything urgent, please speak to a
          qualified healthcare provider — or call your local emergency
          service: <span className="whitespace-nowrap">UK 999</span> ·{' '}
          <span className="whitespace-nowrap">US 911</span> ·{' '}
          <span className="whitespace-nowrap">EU 112</span>.
        </p>

      </div>
    </div>
  );
};

export default Landing;
